import { observable, runInAction } from "mobx"
import { toast } from "sonner"

import { dropSecureStateCookie, SlackSecureCookie } from "@/lib/cookieUtils.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { resolveWithDelay } from "@/lib/promise.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"

import type { Queries } from "@/lib/queries.ts"
import type { MouseEvent } from "react"

const log = getLogger("SlackNotificationsModel")

export class SlackNotificationsModel extends Model {
  readonly authUrl = observable.box<string | null>(null)
  readonly isEnabled = observable.box<boolean | null>(false)
  readonly isLoading = observable.box(true)

  constructor(private readonly queries: Queries) {
    super("SlackNotificationsModel")

    this.sync().catch((err) => {
      toast.error("Failed to load Slack notifications status")
      log.error("Failed to load Slack notifications status", err)
    })
  }

  onEnableNotificationsClick = (event: MouseEvent) => {
    event.preventDefault()
    if (this.authUrl.get()) {
      openLinkInNewWindow(this.authUrl.get()!)
    }
  }

  private sync = async () => {
    const promise = Promise.all([
      this.queries.getSlackNotificationsEnabled(),
      this.queries.apiClient.startSlackUserForNotificationsOAuthFlow()
    ])

    const [isEnabled, authUrl] = await resolveWithDelay(promise, 400)

    runInAction(() => {
      this.authUrl.set(authUrl)
      this.isEnabled.set(isEnabled)
      if (isEnabled) {
        dropSecureStateCookie(SlackSecureCookie)
      }

      this.isLoading.set(false)
    })
  }

  onResetClick = () => {
    void this.queries.apiClient.resetSlackToken()
  }
}